import { useState, useRef } from 'react';
import { CheckCircle, Upload, Play, AlertCircle } from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import { uploadAnalysis, pollUntilDone } from '../api/analysisApi';

// UI ids -> backend INPUT_TYPES enum (config/paths.js) -> flowchart INPUT A/B/C
const INPUT_TYPE_MAP = {
  'short-reads': 'short_reads',     // INPUT A -> SPAdes
  assembled: 'assembled_sequence',  // INPUT B -> straight to BLAST
  'long-reads': 'long_reads',       // INPUT C -> Flye
};

// job.step from the backend, in pipeline order. Index = number of stages completed.
const STEP_INDEX = { queued: 0, assembling: 1, blasting: 2, filtering: 3, done: 4, completed: 4 };

const stagesFor = (inputType) => [
  'Uploading & queuing job',
  inputType === 'assembled'
    ? 'Preparing assembled sequence'
    : inputType === 'long-reads'
    ? 'Genome assembly (Flye)'
    : 'Genome assembly (SPAdes)',
  'BLAST search against Cry database',
  'Filtering hits & compiling report',
];

const AnalysisPage = ({ setActiveTab }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [secondFile, setSecondFile] = useState(null); // R2 — required for short reads (paired-end)
  const [sampleName, setSampleName] = useState('');
  const [inputType, setInputType] = useState('short-reads');
  const [evalue, setEvalue] = useState('1e-5');
  const [minIdentity, setMinIdentity] = useState('90');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [dragActiveR2, setDragActiveR2] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const secondFileInputRef = useRef(null);

  const isShortReads = inputType === 'short-reads';
  const analysisStages = stagesFor(inputType);

  const deriveSampleName = (filename) => filename.replace(/\.[^/.]+(\.gz)?$/i, '');

  const applyFile = (file) => {
    if (!file) return;
    setUploadedFile(file);
    if (!sampleName) setSampleName(deriveSampleName(file.name));
  };

  const handleFileUpload = (event) => applyFile(event.target.files[0]);
  const handleSecondFileUpload = (event) => setSecondFile(event.target.files[0] || null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    applyFile(e.dataTransfer.files?.[0]);
  };

  const handleDropR2 = (e) => {
    e.preventDefault();
    setDragActiveR2(false);
    setSecondFile(e.dataTransfer.files?.[0] || null);
  };

  const selectInputType = (id) => {
    setInputType(id);
    if (id !== 'short-reads') setSecondFile(null);
  };

  const canSubmit = uploadedFile && sampleName.trim() && (!isShortReads || secondFile) && !isAnalyzing;

  const startAnalysis = async () => {
    if (!canSubmit) return;
    setError(null);
    setIsAnalyzing(true);
    setAnalysisStep(0);

    try {
      const files = secondFile ? [uploadedFile, secondFile] : [uploadedFile];
      const { jobId } = await uploadAnalysis({
        inputType: INPUT_TYPE_MAP[inputType],
        sampleName: sampleName.trim(),
        files,
        evalue,
        minIdentity,
      });

      sessionStorage.setItem('cry_last_job_id', jobId);

      await pollUntilDone(jobId, {
        onStatusChange: (status) => setAnalysisStep(STEP_INDEX[status.step] ?? 0),
      });

      setAnalysisStep(analysisStages.length);
      setTimeout(() => {
        setIsAnalyzing(false);
        setActiveTab('results');
      }, 800);
    } catch (err) {
      setIsAnalyzing(false);
      setError(err.message || 'Analysis failed. Please try again.');
    }
  };

  return (
    <div className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">Start Your <span className="text-brand">Analysis</span></h2>
            <p className="text-xl text-slate-600">Upload your genomic data and let our advanced pipeline identify Cry toxin genes</p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-md">
            {/* Input Type Selection */}
            <div className="mb-8">
              <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">Select Input Type</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { id: 'short-reads', label: 'Short Reads', subtitle: 'Illumina (.fastq/.fq)' },
                  { id: 'assembled', label: 'Assembled Genome', subtitle: 'FASTA (.fasta/.fa)' },
                  { id: 'long-reads', label: 'Long Reads', subtitle: 'PacBio/Nanopore' },
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => selectInputType(type.id)}
                    className={`p-4 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 text-left ${
                      inputType === type.id ? 'border-brand bg-brand-tint text-brand scale-[1.02] shadow-sm' : 'border-slate-200 hover:border-slate-300 text-slate-700 bg-white'
                    }`}
                  >
                    <div className="font-semibold">{type.label}</div>
                    <div className="text-sm opacity-75">{type.subtitle}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sample Name */}
            <div className="mb-8">
              <label className="block text-sm text-slate-600 mb-2">Sample Name</label>
              <input
                type="text"
                value={sampleName}
                onChange={(e) => setSampleName(e.target.value)}
                placeholder="e.g. Bt_isolate_42"
                className="input-anim w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-800 focus:border-brand focus:outline-none transition-all"
              />
              <p className="text-xs text-slate-400 mt-1">Used to name your output files (e.g. {sampleName || 'sample'}.fasta, CryToxin.tsv)</p>
            </div>

            {/* File Upload */}
            <div className="mb-8">
              <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">Upload Your Data</h3>

              <label className="block text-sm text-slate-600 mb-2">
                {isShortReads ? 'Read 1 (R1)' : 'File'}
              </label>
              <div
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 ${
                  dragActive ? 'border-brand bg-brand-tint scale-[1.01]' : uploadedFile ? 'border-brand bg-brand-tint' : 'border-slate-300 hover:border-brand hover:bg-slate-50'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                {uploadedFile ? (
                  <CheckCircle className="w-12 h-12 text-brand mx-auto mb-4 animate-pop-in" />
                ) : (
                  <Upload className={`w-12 h-12 text-slate-400 mx-auto mb-4 transition-transform ${dragActive ? 'animate-bounce' : 'animate-bob'}`} />
                )}
                <p className="text-slate-700 mb-2">{uploadedFile ? `Selected: ${uploadedFile.name}` : dragActive ? 'Drop your file to upload' : 'Click to upload or drag and drop'}</p>
                <p className="text-sm text-slate-400">Supports FASTQ, FASTA, and compressed files</p>
                <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" accept=".fastq,.fq,.fasta,.fa,.gz" />
              </div>

              {isShortReads && (
                <div className="mt-6">
                  <label className="block text-sm text-slate-600 mb-2">
                    Read 2 (R2) <span className="text-red-500">*required</span> — paired-end short reads
                  </label>
                  <div
                    className={`relative border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 ${
                      dragActiveR2 ? 'border-brand bg-brand-tint scale-[1.01]' : secondFile ? 'border-brand bg-brand-tint' : 'border-slate-300 hover:border-brand hover:bg-slate-50'
                    }`}
                    onClick={() => secondFileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActiveR2(true);
                    }}
                    onDragLeave={() => setDragActiveR2(false)}
                    onDrop={handleDropR2}
                  >
                    {secondFile ? (
                      <CheckCircle className="w-8 h-8 text-brand mx-auto mb-2 animate-pop-in" />
                    ) : (
                      <Upload className={`w-8 h-8 text-slate-400 mx-auto mb-2 transition-transform ${dragActiveR2 ? 'animate-bounce' : ''}`} />
                    )}
                    <p className="text-sm text-slate-700">{secondFile ? `Selected: ${secondFile.name}` : 'Click to upload or drag and drop R2'}</p>
                    <input ref={secondFileInputRef} type="file" onChange={handleSecondFileUpload} className="hidden" accept=".fastq,.fq,.gz" />
                  </div>
                </div>
              )}
            </div>

            {/* Analysis Parameters */}
            <div className="mb-8">
              <h3 className="font-display text-2xl font-semibold text-slate-900 mb-6">Analysis Parameters</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-slate-600 mb-2">E-value Threshold</label>
                  <select
                    value={evalue}
                    onChange={(e) => setEvalue(e.target.value)}
                    className="input-anim w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-800 focus:border-brand focus:outline-none transition-all"
                  >
                    <option value="1e-5">1e-5 (Default)</option>
                    <option value="1e-3">1e-3</option>
                    <option value="1e-10">1e-10</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Sequence Identity (%)</label>
                  <select
                    value={minIdentity}
                    onChange={(e) => setMinIdentity(e.target.value)}
                    className="input-anim w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-800 focus:border-brand focus:outline-none transition-all"
                  >
                    <option value="90">90% (Default)</option>
                    <option value="70">70%</option>
                    <option value="80">80%</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Error banner */}
            {error && (
              <div className="mb-8 flex items-start space-x-3 bg-red-50 border border-red-200 rounded-2xl p-4 animate-fade-in-up">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Staged analysis progress */}
            {isAnalyzing && (
              <div className="mb-8 animate-fade-in-up">
                <div className="bg-slate-50 rounded-2xl border border-blue-100 p-6">
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mb-6">
                    <div className="h-full bg-brand rounded-full transition-all duration-700 ease-out" style={{ width: `${(analysisStep / analysisStages.length) * 100}%` }}></div>
                  </div>
                  <div className="space-y-3">
                    {analysisStages.map((stage, i) => {
                      const done = i < analysisStep;
                      const active = i === analysisStep;
                      return (
                        <div key={i} className="flex items-center space-x-3">
                          {done ? (
                            <CheckCircle className="w-5 h-5 text-brand animate-pop-in" />
                          ) : active ? (
                            <div className="w-5 h-5 rounded-full border-2 border-brand border-t-transparent animate-spin"></div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-slate-300"></div>
                          )}
                          <span className={`text-sm ${active ? 'text-brand font-medium' : done ? 'text-slate-500' : 'text-slate-400'}`}>{stage}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Start Analysis */}
            <div className="text-center">
              <button
                onClick={startAnalysis}
                disabled={!canSubmit}
                className="brand-btn px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:scale-105 flex items-center space-x-2 mx-auto"
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    <span>Start Analysis</span>
                  </>
                )}
              </button>
              {isShortReads && !secondFile && uploadedFile && (
                <p className="text-xs text-slate-400 mt-3">Read 2 is required for short reads before you can start.</p>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default AnalysisPage;